"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import Hls from "hls.js";
import { useResolvedVideoUrl } from "@/hooks/useResolvedVideoUrl";

// Single source of truth for session-level mute state
let globalMuteState = true;

export type UseVideoPlayerOptions = {
  /** Raw video URL (v3 URLs are resolved automatically) */
  src: string;
  /** When false, tears down HLS and pauses. Default: true */
  isActive?: boolean;
  /** Auto-play when ready. Default: true */
  autoPlay?: boolean;
};

export function useVideoPlayer({
  src,
  isActive = true,
  autoPlay = true,
}: UseVideoPlayerOptions) {
  const { url: resolvedUrl, isLoading: isUrlLoading } =
    useResolvedVideoUrl(src);

  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(globalMuteState);

  // --- HLS setup / teardown ---
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isUrlLoading || !resolvedUrl) return;

    if (!isActive) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.pause();
      video.removeAttribute("src");
      video.load();
      setIsPlaying(false);
      return;
    }

    const isHlsUrl = resolvedUrl.includes(".m3u8");

    if (isHlsUrl) {
      if (Hls.isSupported()) {
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(resolvedUrl);
        hls.attachMedia(video);
        if (autoPlay) {
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play().catch(() => {});
            setIsPlaying(true);
          });
        }
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = resolvedUrl;
        if (autoPlay) {
          video.play().catch(() => {});
          setIsPlaying(true);
        }
      }
    } else {
      video.src = resolvedUrl;
      if (autoPlay) {
        video.play().catch(() => {});
        setIsPlaying(true);
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [resolvedUrl, isUrlLoading, isActive, autoPlay]);

  // --- Progress tracking ---
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let animationFrameId: number;
    let lastTime = 0;

    const updateProgress = () => {
      if (video.duration) {
        if (video.currentTime < lastTime) {
          setProgress(0);
        }
        lastTime = video.currentTime;
        setProgress((video.currentTime / video.duration) * 100);
      }
      animationFrameId = requestAnimationFrame(updateProgress);
    };

    const handlePlay = () => {
      animationFrameId = requestAnimationFrame(updateProgress);
    };

    const handlePause = () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };

    const handleSeeked = () => {
      if (video.currentTime < 0.1) setProgress(0);
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handlePause);
    video.addEventListener("seeked", handleSeeked);

    if (!video.paused) handlePlay();

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handlePause);
      video.removeEventListener("seeked", handleSeeked);
    };
  }, []);

  // --- Cleanup on unmount ---
  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    };
  }, []);

  // --- Play/pause toggle ---
  const togglePlay = useCallback(
    (force?: boolean) => {
      const video = videoRef.current;
      if (!video) return;

      setShowIcon(true);

      const shouldPlay = force !== undefined ? force : !isPlaying;

      if (shouldPlay) {
        video.play().catch((error) => {
          console.log("Video play failed:", error);
        });
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }

      setTimeout(() => setShowIcon(false), 1000);
    },
    [isPlaying],
  );

  // --- Seek via progress bar click ---
  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const video = videoRef.current;
      if (!video) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      video.currentTime = percentage * video.duration;
    },
    [],
  );

  // --- Mute toggle ---
  const handleMuteToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newMutedState = !globalMuteState;
    setIsMuted(newMutedState);
    globalMuteState = newMutedState;
  }, []);

  // --- Attach HLS to an arbitrary video element (for dual-video cases) ---
  const attachHlsToElement = useCallback(
    (video: HTMLVideoElement): (() => void) => {
      let hls: Hls | null = null;
      const isHlsUrl = resolvedUrl.includes(".m3u8");

      if (!isHlsUrl) {
        video.src = resolvedUrl;
        return () => {};
      }

      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(resolvedUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = resolvedUrl;
      }

      return () => {
        if (hls) {
          hls.destroy();
        }
      };
    },
    [resolvedUrl],
  );

  return {
    videoRef,
    resolvedUrl,
    isUrlLoading,
    isPlaying,
    showIcon,
    progress,
    isMuted,
    togglePlay,
    handleProgressClick,
    handleMuteToggle,
    attachHlsToElement,
  };
}
